import {
  Tag, Code, GraduationCap, Phone, Briefcase,
  Handshake, Article, GitBranch, Eye, Users,
  VideoCamera, Envelope, TreeStructure, Table,
  Cube, Download,
  // Skill-area icons
  Translate, PaintBrush, Terminal, Cloud, FileJs,
  Database, FileCode, Atom, Palette, FileTs,
  Package, Circuitry,
} from '@phosphor-icons/react';
import Section from '@/components/Section';
import ProjectCard from '@/components/ProjectCard';
import VideoEmbed from '@/components/VideoEmbed';
import ArticleLink from '@/components/ArticleLink';

const skills = [
  { name: 'Babel', Icon: Translate },
  { name: 'CSS3', Icon: PaintBrush },
  { name: 'Git', Icon: GitBranch },
  { name: 'Grunt', Icon: Terminal },
  { name: 'Gulp', Icon: Terminal },
  { name: 'Heroku', Icon: Cloud },
  { name: 'HTML5', Icon: Code },
  { name: 'JavaScript', Icon: FileJs },
  { name: 'PostgreSQL', Icon: Database },
  { name: 'MySQL', Icon: Database },
  { name: 'PHP', Icon: FileCode },
  { name: 'jQuery', Icon: FileJs },
  { name: 'React', Icon: Atom },
  { name: 'Sass', Icon: Palette },
  { name: 'TypeScript', Icon: FileTs },
  { name: 'webpack', Icon: Package },
  { name: 'Node.js', Icon: Circuitry },
];

export default function Home() {
  return (
    <main id="stage">
      {/* ── Services ── */}
      <Section id="intro" title="Services" Icon={Tag}>
        <div className="legible">
          <p className="text-secondary">
            Over the past 10 years I have bridged design and development by specializing in
            Graphic Design and Full Stack Web Development with a strong emphasis on reliable
            performance, speed, accessibility, and web standards.
          </p>
          <p className="text-secondary mt-3">
            These days I specialize in creating hyper performant React web apps.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-8">
          {/* Web Development */}
          <div>
            <h3 className="text-lg font-semibold mb-4 inline-flex items-center gap-2">
              <Code size={22} weight="bold" aria-hidden="true" />
              Web Development
            </h3>
            <div className="grid grid-cols-4 sm:grid-cols-5 gap-3">
              {skills.map(({ name, Icon }) => (
                <div key={name} className="skill-item">
                  <Icon size={28} weight="duotone" aria-hidden="true" />
                  <span>{name}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Graphic Design */}
          <div>
            <h3 className="text-lg font-semibold mb-4 inline-flex items-center gap-2">
              <GraduationCap size={22} weight="bold" aria-hidden="true" />
              Graphic Design
            </h3>
            <p className="text-secondary text-sm">
              Bachelor of Science Degree in Graphic Design
            </p>
          </div>
        </div>
      </Section>

      {/* ── Portfolio ── */}
      <Section id="portfolio" title="Portfolio" Icon={Briefcase}>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <ProjectCard
            title="Eureka Media Browser"
            href="https://github.com/jpdevries/eureka#eureka-"
            Icon={TreeStructure}
            description="A mobile-first and inclusively designed media browser; progressively enhanced with React and server side rendering provided by a Nope/Express backend."
            tags={['#react', '#redux', '#node', '#a11y']}
          />
          <ProjectCard
            title="Accessible Data Grid"
            href="https://github.com/jpdevries/a11y-datagrid#demo"
            Icon={Table}
            description="With plain HTML, mobile, keyboard, and screen reader support this is a progressively enhanced data grid anyone can use."
            tags={['#react', '#redux', '#a11y']}
          />
          <ProjectCard
            title="Makeanico"
            href="https://github.com/jpdevries/makeanico#makeanico"
            Icon={Cube}
            description="Weighing in at under 10kB, this progressively enhanced web app enables anyone to create a 16x16 favicon.ico graphic."
            tags={['#10kapart', '#a11y', '#vanillajs']}
          />
        </div>
      </Section>

      {/* ── Consulting ── */}
      <Section id="consulting" title="Consulting" Icon={Phone}>
        <p className="text-secondary legible">
          Front End or Back End, design or development, guidance is just a consult away.
        </p>
        <a href="mailto:mail@devries.jp?subject=Consulting" className="btn mt-4 inline-flex">
          Schedule a consult
        </a>
      </Section>

      {/* ── Resume ── */}
      <Section id="resume" title="Resume" Icon={Briefcase}>
        <a href="/assets/pdf/devries_resume.pdf" className="btn inline-flex">
          <Download size={18} weight="bold" aria-hidden="true" />
          PDF Download
        </a>
      </Section>

      {/* ── Hiring note ── */}
      <div className="px-6 py-8">
        <aside className="max-w-sm mx-auto card rounded-lg p-6 text-center">
          <Handshake size={32} weight="duotone" className="mx-auto mb-3 text-accent" aria-hidden="true" />
          <p className="text-sm text-secondary mb-4">
            We're in luck. I'm currently accepting contracts for Web Development &amp; Consulting.
          </p>
          <a href="mailto:mail@devries.jp?subject=Contract%20Work" className="btn text-sm inline-flex">
            Hire Me
          </a>
        </aside>
      </div>

      {/* ── Mentoring ── */}
      <Section id="mentoring" title="Mentoring" Icon={Users}>
        <p className="text-secondary legible">
          My experience as an educator began in 2009 as a College Professor at the Art
          Institute of Portland. Today, as a{' '}
          <a href="https://www.thinkful.com/mentors/" target="_blank" rel="noopener noreferrer" className="link">
            coding bootcamp mentor at Thinkful
          </a>{' '}
          I have the privilege of guiding dozens of Web Development students along their
          journey. I believe the best way to master a skill is to teach it. Through my
          students, I have learned more about myself, my craft, and my aspirations as an
          architect of the world&nbsp;wide&nbsp;web.
        </p>
      </Section>

      {/* ── Musings ── */}
      <Section id="musings" title="Musings" Icon={Article}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <ArticleLink title="Preparing React Components for Server-Side Rendering" href="https://medium.com/front-end-hacking/preparing-react-components-for-server-side-rendering-18901d09784c" />
          <ArticleLink title="Count the Clicks" href="https://medium.com/@jpdevries/count-the-clicks-60719720f88b" />
          <ArticleLink title="Give your Forms some Form" href="https://medium.com/front-end-hacking/give-your-forms-some-form-2ec73cb36981" />
          <ArticleLink title="CSS is !important" href="https://medium.com/markuptips/css-is-important-960a9921e454" />
          <ArticleLink title="Unfolding Critical CSS" href="https://medium.com/markuptips/unfolding-critical-css-91619401b4e" />
          <ArticleLink title="Let us Learn from Wordpress" href="https://medium.com/markuptips/let-us-learn-from-wordpress-28b0803530d8" />
          <ArticleLink title="Accessibility is Water" href="https://modx.today/posts/2016/02/accessibility-is-water" />
          <ArticleLink title="Love What You Do" href="https://modx.today/posts/2016/11/love-what-you-do" />
        </div>
      </Section>

      {/* ── Open Source ── */}
      <Section id="opensource" title="Open Source" Icon={GitBranch}>
        <p className="text-secondary legible mb-6">
          I enjoy contributing to various open source projects; after all open software is
          what makes the world go around.{' '}
          <a href="https://github.com/pulls?q=is%3Apr+author%3Ajpdevries+is%3Acopen" target="_blank" rel="noopener noreferrer" className="link">
            View my pull requests
          </a>
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <ProjectCard
            title="MODX Next Setup"
            href="https://jpdevries.github.io/modx-setup/"
            description="This all-new MODX concept originated at the MODX Meetup in Malta and aims to make the unboxing experience of MODX more accessible to end users."
            tags={['#MODX', '#a11y']}
          />
          <ProjectCard
            title="Accessible Data Grid"
            href="https://github.com/jpdevries/a11y-datagrid#demo"
            Icon={Table}
            description="This mobile-first data grid meets accessibility success criteria while being progressively enhanced with React/Redux."
            tags={['#react', '#redux', '#a11y']}
          />
        </div>
      </Section>

      {/* ── Accessibility ── */}
      <Section id="accessibility" title="Accessibility" Icon={Eye}>
        <p className="text-secondary legible">
          Each day I work towards a web for everyone by studying accessible inclusive design
          patterns and mentoring students.
        </p>
        <a href="mailto:mail@devries.jp?subject=Accessibility%20Consultation" className="btn mt-4 inline-flex">
          Schedule an Accessibility Consult
        </a>
      </Section>

      {/* ── Markup.Tips ── */}
      <Section id="markuptips" title="Markup.Tips" Icon={Code}>
        <p className="text-secondary legible mb-4">
          Over at{' '}
          <a href="https://markup.tips" target="_blank" rel="noopener noreferrer" className="link">
            Markup.Tips
          </a>{' '}
          I share tools, tips, and components.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <ArticleLink
            title="Silence Your CSS Footprint With Sass Silent Classes"
            href="https://www.thinkful.com/projects/silence-your-css-footprint-with-sass-silent-classes-457"
            tags={['#scss']}
          />
          <ArticleLink
            title="Animating SVG Artwork with <canvas> & VanillaJS"
            href="https://markup.tips/blog/2015/animating-svg-artwork-with-canvas-and-vanillajs.html#focus"
            tags={['#svg', '#canvas', '#vanillajs']}
          />
          <ArticleLink
            title="Grunting Icons into SVG Sprites"
            href="https://markup.tips/projects/grunting-icons-into-svg-sprites.html#focus"
            tags={['#node', '#grunt', '#svg', '#a11y']}
          />
          <ArticleLink
            title="Bringing SVG Graphics to Life with React"
            href="https://markup.tips/projects/thinkful-bringing-svg-graphics-to-life-with-react.html#focus"
            tags={['#react', '#svg']}
          />
        </div>
      </Section>

      {/* ── Workshops ── */}
      <Section id="workshops" title="Workshops" Icon={Users}>
        <div className="max-w-2xl">
          <VideoEmbed
            title="Creating Hyper Performant Web Apps with React"
            src="https://www.youtube.com/embed/Nu9-ea75GzQ"
            tags={['#youtube']}
          />
        </div>
      </Section>

      {/* ── Screencasts ── */}
      <Section id="screencasts" title="Screencasts" Icon={VideoCamera}>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          <VideoEmbed title="MODX Create Resource Concept" src="https://player.vimeo.com/video/219024629" tags={['#MODX', '#vanillajs']} />
          <VideoEmbed title="All-new MODX Setup Concept" src="https://player.vimeo.com/video/218397030" tags={['#MODX', '#a11y']} />
          <VideoEmbed title="MODX Setup Permissions" src="https://player.vimeo.com/video/217647956" tags={['#MODX', '#a11y']} />
          <VideoEmbed title="Saving Data with the Save Data Header" src="https://player.vimeo.com/video/215494474" tags={['#a11y']} />
          <VideoEmbed title="Architecting a Futuristic MODX Manager" src="https://player.vimeo.com/video/195648795" tags={['#MODX', '#design']} />
          <VideoEmbed title="Manage Users CMP" src="https://player.vimeo.com/video/176084699" tags={['#MODX', '#react']} />
          <VideoEmbed title="Benefits of Designing HTML-first" src="https://player.vimeo.com/video/189324043" tags={['#HTML5', '#design', '#a11y', '#seo']} />
        </div>
      </Section>

      {/* ── Contact ── */}
      <Section id="contact" title="Contact" Icon={Envelope}>
        <p className="text-secondary legible">My inbox is always open.</p>
        <a href="mailto:mail@devries.jp?subject=Consulting" className="btn mt-4 inline-flex">
          Drop me a line
        </a>
      </Section>
    </main>
  );
}
